/**
 * Generic object pool for reusing objects instead of creating new ones
 * Reduces garbage collection pressure and improves performance
 */
export class ObjectPool<T extends object> {
  private pool: T[] = []
  private pooled = new WeakSet<T>() // guards against double-release aliasing
  private factory: () => T
  private resetFn: (obj: T) => void
  private maxSize: number

  constructor(
    factory: () => T,
    resetFn: (obj: T) => void,
    initialSize: number = 0,
    maxSize: number = 1000
  ) {
    this.factory = factory
    this.resetFn = resetFn
    this.maxSize = maxSize

    // Pre-allocate initial pool
    for (let i = 0; i < initialSize; i++) {
      this.pool.push(factory())
    }
  }

  /**
   * Get an object from the pool, or create a new one if empty
   */
  acquire(): T {
    if (this.pool.length > 0) {
      const obj = this.pool.pop()!
      this.pooled.delete(obj)
      return obj
    }
    return this.factory()
  }

  /**
   * Return an object to the pool for reuse
   */
  release(obj: T): void {
    // Releasing the same object twice would hand it out to two owners later
    if (this.pooled.has(obj)) return
    if (this.pool.length < this.maxSize) {
      this.resetFn(obj)
      this.pool.push(obj)
      this.pooled.add(obj)
    }
    // If pool is at max size, let the object be garbage collected
  }

  /**
   * Get current pool size
   */
  get size(): number {
    return this.pool.length
  }

  /**
   * Clear the pool
   */
  clear(): void {
    this.pool.length = 0
    this.pooled = new WeakSet<T>()
  }
}
